import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Row, Col } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";

import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import { FormikErrorText } from "../../../components/FormikErrorText";
import { AutoComplete } from "../../../components/AutoComplete";
import { customStyles } from "../../UserManagement/CustomStyles";
import { PLACEHOLDERS, LABELS, NAMES } from "../Config";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { GetAuthor, GetNarrator } from "../../../api/api.service";
import UploadContainer from "../UploadContainer";
import { mapToOptions, truncateString } from "../../../utils/commonUtils";

import { ONBOARDING_AUDIO_FORM_VALIDATION_SCHEMA } from "../FormValidation";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { FileUploader } from "../../../components/FileUploader";
import { MediaType } from "../../../constants";
import { USER_STATUS } from "../../../constants/UserStatus";

const EDIT = "edit";
const CHARACTER_LIMIT = 30;

let {
    TITLE,
    DESCRIPTION,
    AUTHOR,
    NARRATOR,
    SESSION_SUCCESS_MESSAGE,
    UPLOAD_AUDIO,
    UPLOAD_THUMBNAIL,
    UPLOAD_IMAGE
} = LABELS;

const OnboardingForm = ({ mode, isLoading, data, onUpdateAudio, onAddAudio, isDisabled }) => {
    const queryClient = useQueryClient();
    let isAudioEditable = mode === EDIT;
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreAuthors, setHasMoreAuthors] = useState(true);
    const [hasMoreNarrators, setHasMoreNarrators] = useState(true);
    const [authorOptions, setAuthorOptions] = useState([]);
    const [narratorOptions, setNarratorOptions] = useState([]);
    const { useFetch } = useRequestProcessor();

    const updatedInitialValues = {
        [NAMES[TITLE]]: data?.title,
        [NAMES[DESCRIPTION]]: data?.description,
        [NAMES[AUTHOR]]: isAudioEditable
            ? { value: data?.author?.id, label: data?.author?.name }
            : null,
        [NAMES[NARRATOR]]: isAudioEditable
            ? { value: data?.narrator?.id, label: data?.narrator?.name }
            : null,
        [NAMES[UPLOAD_AUDIO]]: data?.audios[0]?.audioMedia || null,
        [NAMES[UPLOAD_THUMBNAIL]]: data?.croppedThumbnail || null,
        [NAMES[UPLOAD_IMAGE]]: data?.thumbnail || null,
        [NAMES[SESSION_SUCCESS_MESSAGE]]: data?.audios[0]?.completionMessage
    };

    const formik = useFormik({
        initialValues: updatedInitialValues,
        validationSchema: ONBOARDING_AUDIO_FORM_VALIDATION_SCHEMA,
        onSubmit: (values) => {
            if (isAudioEditable) {
                onUpdateAudio(values);
            } else {
                onAddAudio(values);
            }
        }
    });

    useEffect(() => {
        queryClient.invalidateQueries(getKey([MODULE_KEYS.AUTHOR, currentPage], {}));
        queryClient.invalidateQueries(getKey([MODULE_KEYS.NARRATOR, currentPage], {}));

        return () => {
            setAuthorOptions([]);
            setNarratorOptions([]);
        };
    }, []);

    const { data: authors, isFetching: isFetchingAuthors } = useFetch(
        getKey([MODULE_KEYS.AUTHOR, currentPage], {}),
        async ({ signal }) =>
            await GetAuthor({
                params: { page: currentPage, limit: 10, status: USER_STATUS.ACTIVE },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreAuthors, staleTime: 0, cacheTime: 0 }
    );

    const { data: narrators, isFetching: isFetchingNarrators } = useFetch(
        getKey([MODULE_KEYS.NARRATOR, currentPage], {}),
        async ({ signal }) =>
            await GetNarrator({
                params: { page: currentPage, limit: 10, status: USER_STATUS.ACTIVE },
                signal
            }),
        { keepPreviousData: true, enabled: hasMoreNarrators, staleTime: 0, cacheTime: 0 }
    );

    useEffect(() => {
        if (authors?.data) {
            const newOptions = mapToOptions(authors.data, "name", "id").map((option) => ({
                ...option,
                label: truncateString(option.label, CHARACTER_LIMIT)
            }));

            setAuthorOptions((prev) => {
                const uniqueOptions = [...prev, ...newOptions].reduce((acc, option) => {
                    if (!acc.some((item) => item.value === option.value)) {
                        acc.push(option);
                    }
                    return acc;
                }, []);
                return uniqueOptions;
            });

            if (authorOptions.length + newOptions.length >= authors.total) {
                setHasMoreAuthors(false);
            }
        }
    }, [authors]);

    useEffect(() => {
        if (narrators?.data) {
            const newOptions = mapToOptions(narrators.data, "name", "id").map((option) => ({
                ...option,
                label: truncateString(option.label, CHARACTER_LIMIT)
            }));

            setNarratorOptions((prev) => {
                const uniqueOptions = [...prev, ...newOptions].reduce((acc, option) => {
                    if (!acc.some((item) => item.value === option.value)) {
                        acc.push(option);
                    }
                    return acc;
                }, []);
                return uniqueOptions;
            });

            if (narratorOptions.length + newOptions.length >= narrators.total) {
                setHasMoreNarrators(false);
            }
        }
    }, [narrators]);

    const handleScroll = (e, type) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            if (type === "author" && hasMoreAuthors && !isFetchingAuthors) {
                setCurrentPage((prev) => prev + 1);
            }
            if (type === "narrator" && hasMoreNarrators && !isFetchingNarrators) {
                setCurrentPage((prev) => prev + 1);
            }
        }
    };

    const handleFileUpload = async (file, exactFile, fieldName) => {
        formik.setFieldValue(NAMES[fieldName], { file, exactFile }); // Store file and mediaId in form
    };
    const handleFileRemove = (fieldName) => {
        formik.setFieldValue(NAMES[fieldName], null); // Clear file from form
    };

    const handleCancel = () => {
        if (isLoading) return;
        formik.resetForm();
        history.push(APP_ROUTES.AUDIO_CONTENT_MANAGEMENT_ONBOARDING);
    };

    return (
        <form onSubmit={formik.handleSubmit} className="p-4">
            <Row>
                <Col md={12}>
                    <TextInput
                        placeholder={PLACEHOLDERS[TITLE]}
                        label={TITLE}
                        name={NAMES[TITLE]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[NAMES[TITLE]]}
                        className="hide-default-icon"
                        disabled={isDisabled}
                        autoFocus
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[TITLE]} />
                </Col>
            </Row>

            <Row className="my-4">
                <TextInput
                    placeholder={PLACEHOLDERS[DESCRIPTION]}
                    label={DESCRIPTION}
                    name={NAMES[DESCRIPTION]}
                    type="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[NAMES[DESCRIPTION]]}
                    disabled={isDisabled}
                    className="hide-default-icon"
                />
                <FormikErrorText formikInstance={formik} fieldName={NAMES[DESCRIPTION]} />
            </Row>

            {/* Author & Narrator */}
            <Row className="my-4">
                <Col md={6}>
                    <AutoComplete
                        isDisabled={isDisabled}
                        classNamePrefix="status-header-search-admin"
                        isLoading={isFetchingAuthors}
                        handleScroll={(e) => handleScroll(e, "author")}
                        label={AUTHOR}
                        value={
                            isAudioEditable
                                ? authorOptions.find(
                                      (option) =>
                                          option.value === formik.values[NAMES[AUTHOR]]?.value
                                  )
                                : formik.values[NAMES[AUTHOR]]
                        }
                        onChange={(option) => formik.setFieldValue(NAMES[AUTHOR], option || null)}
                        onBlur={() => formik.setFieldTouched(NAMES[AUTHOR])}
                        placeholder={PLACEHOLDERS[AUTHOR]}
                        options={authorOptions}
                        maxMenuHeight={220}
                        customStyles={customStyles.filter}
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[AUTHOR]} />
                </Col>

                <Col md={6}>
                    <AutoComplete
                        isDisabled={isDisabled}
                        classNamePrefix="status-header-search-admin"
                        customStyles={customStyles.filter}
                        isLoading={isFetchingNarrators}
                        handleScroll={(e) => handleScroll(e, "narrator")}
                        label={NARRATOR}
                        value={
                            isAudioEditable
                                ? narratorOptions.find(
                                      (option) =>
                                          option.value === formik.values[NAMES[NARRATOR]]?.value
                                  )
                                : formik.values[NAMES[NARRATOR]]
                        }
                        onChange={(option) => formik.setFieldValue(NAMES[NARRATOR], option || null)}
                        onBlur={() => formik.setFieldTouched(NAMES[NARRATOR])}
                        placeholder={PLACEHOLDERS[NARRATOR]}
                        options={narratorOptions}
                        isClearable
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[NARRATOR]} />
                </Col>
            </Row>

            <UploadContainer
                formik={formik}
                setFieldValue={formik.setFieldValue}
                formValues={formik.values}
                data={data && data}
            />
            <Row className="my-4">
                <Col md={12}>
                    <p className="mb-2 input-label">{UPLOAD_IMAGE}</p>
                    <FileUploader
                        fileType={MediaType.IMAGE}
                        supportedFormats="Supported format: .jpg, .png Max file size: 25MB"
                        acceptedFileTypes={["jpg", "png"]} // Define accepted types
                        maxFileSize={1} // Limit size to 1MB
                        onFileUpload={(file, exactFile) =>
                            handleFileUpload(file, exactFile, UPLOAD_IMAGE)
                        }
                        onFileRemove={() => handleFileRemove(UPLOAD_IMAGE)}
                        value={formik.values[NAMES[UPLOAD_IMAGE]]} // Display current file
                        previewUrl={
                            data?.thumbnail
                                ? `${data?.thumbnail?.location}${data?.thumbnail?.thumbPath}`
                                : null
                        } // Existing thumbnail preview
                        previewSize={data?.thumbnail?.size}
                    />
                    <FormikErrorText formikInstance={formik} fieldName={NAMES[UPLOAD_IMAGE]} />
                </Col>
            </Row>

            <Row className="my-4">
                <TextInput
                    disabled={isDisabled}
                    placeholder={PLACEHOLDERS[SESSION_SUCCESS_MESSAGE]}
                    label={SESSION_SUCCESS_MESSAGE}
                    name={NAMES[SESSION_SUCCESS_MESSAGE]}
                    type="textarea"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[NAMES[SESSION_SUCCESS_MESSAGE]]}
                    className="hide-default-icon"
                />
                <FormikErrorText
                    formikInstance={formik}
                    fieldName={NAMES[SESSION_SUCCESS_MESSAGE]}
                />
            </Row>

            <div className="d-flex justify-content-end w-100 my-2 mt-4">
                <CustomButton
                    type="button"
                    title="Cancel"
                    className="me-3 custom-btn-sm"
                    color="primary"
                    outline
                    onClick={handleCancel}
                    disabled={isLoading}
                />
                <CustomButton
                    loading={isLoading}
                    type="submit"
                    title={mode === "add" ? "Add Audio" : "Update Audio"}
                    color="primary"
                    className="shadow-theme grad-primary"
                />
            </div>
        </form>
    );
};

export default OnboardingForm;
